<script lang="ts">
  /**
   * SetGroupRibbon — a single set rendered as a row of stroke chips.
   *
   * Used by the Sets-Discovered ledger to show the visual identity of each set
   * without the tap-to-open affordance (that's a future drill-overlay step).
   *
   * Intentionally minimal: only the chip row (no label line, no rest gap).
   * The label lives in the parent row. MAX_CHIPS and the +N overflow chip mirror
   * SetRibbon.svelte exactly so both stay visually identical.
   *
   * Visibility (2026-06 low-vision blind-review BLOCKER): every chip carries a
   * crisp white hairline border so its geometry always reads on the near-black
   * canvas, AND the dark stroke FILLS were lifted to visible mid-slate values in
   * designTokens.ts (the old inset-box-shadow approach was not perceptible).
   *
   * `label` (VP common-sense panel, club-pool-swims round, 2026-07-23): an
   * opt-in compact caption line ABOVE the chip row — "<name> · <meters>m", or
   * "Lone <stroke> · <meters>m" for a single-length set (mirrors SetRibbon's
   * own lone-length copy, just smaller). Default false so the two existing
   * bare-chip callers (SetsDiscoveredSheet, SetHistorySheet — both already
   * show the name via their OWN row text) render byte-identical to before.
   * The Club "this morning" pool strip is the only caller that opts in: 4/4
   * panelists read the unlabeled chip wall as "confetti". Deliberately NOT the
   * full SetRibbon treatment (no rest gaps, no tap target, no chevron) — Scott
   * wants the club glance MORE condensed than the Swim tab, not a second
   * SetRibbon.
   */
  import type { SetGroup } from "../lib/lapAnalysis";
  import type { Lap } from "../lib/laps";
  import type { ChipShade } from "../lib/setPage";
  import { contrastTextOn, strokeChipShort, strokeColor } from "../lib/designTokens";

  /** Match SetRibbon's overflow threshold. */
  const MAX_CHIPS = 22;

  interface Props {
    setGroup: SetGroup;
    /** Drill-view treatment: bigger chips so the set reads as a hero, not a key. */
    large?: boolean;
    /**
     * Collapse a single-stroke run into ONE solid stroke-colored band with a
     * "×N" length count instead of N identical chips. A wall of identical chips
     * reads as a render bug; the band reads as "one stroke, N lengths". Set by
     * the ledger for the generic single-stroke gem (singleStrokeBlock).
     */
    singleBand?: boolean;
    /** Compact "<name> · <meters>m" caption above the chips. See doc above. */
    label?: boolean;
    /** The stroke's short name on each chip, so color is never the only cue
     *  (the set page's definition, drawn large). Default false: unchanged. */
    letters?: boolean;
    /** Chips before the "+N more" chip. The set page's compare rows lower it
     *  to stay on one line; default is SetRibbon's threshold. */
    maxChips?: number;
    /** Per-chip opacity and saturation: the set page's pace shading (Scott,
     *  2026-09-11: "Shade chips by pace"). Default: no shade set, every caller unchanged.
     *  A lone length takes its one lap's shade; a singleBand band stands for many
     *  lengths, so no one pace, and is never shaded. */
    chipShade?: (lap: Lap) => ChipShade;
  }
  let { setGroup, large = false, singleBand = false, label = false, letters = false, maxChips = MAX_CHIPS, chipShade }: Props = $props();

  const shadeFilter = (s: ChipShade | undefined) => (s && s.saturate < 1 ? `saturate(${s.saturate})` : undefined);

  // The band's color is the run's single stroke (first lap is representative).
  let bandColor = $derived(strokeColor(setGroup.laps[0]?.strokeStyle));
  let lengthCount = $derived(setGroup.laps.length);
  let meters = $derived(Math.round(setGroup.meters));
</script>

<div class="sg-set">
  {#if label}
    <!-- Real, announced text (NOT aria-hidden) — the chips below are decorative,
         this caption is the actual information (SetRibbon parity). -->
    <p class="sg-label">
      {#if setGroup.isLoneLength && !singleBand}
        Lone {setGroup.label} · {meters}m
      {:else}
        <strong>{setGroup.label}</strong> · {meters}m
      {/if}
    </p>
  {/if}
  <div class="chips-row" class:large class:letters aria-hidden="true">
    {#if singleBand && lengthCount > 0}
      <!-- One solid band for a single-stroke run, "×N" length count overlaid. -->
      <span class="band" style:background={bandColor}>
        <span class="band-count">×{lengthCount}</span>
      </span>
    {:else if setGroup.isLoneLength}
      {@const lone = setGroup.laps[0]}
      {@const shade = lone ? chipShade?.(lone) : undefined}
      <span class="chip chip-lone" style:background={strokeColor(lone?.strokeStyle)} style:opacity={shade?.opacity} style:filter={shadeFilter(shade)}></span>
    {:else}
      {#each setGroup.laps.slice(0, maxChips) as lap (lap.index)}
        {@const fill = strokeColor(lap.strokeStyle)}
        {@const shade = chipShade?.(lap)}
        <span class="chip" style:background={fill} style:opacity={shade?.opacity} style:filter={shadeFilter(shade)}>
          {#if letters}<span class="chip-letter" style:color={contrastTextOn(fill)}>{strokeChipShort(lap.strokeStyle)}</span>{/if}
        </span>
      {/each}
      {#if setGroup.laps.length > maxChips}
        <span class="chip chip-more">+{setGroup.laps.length - maxChips} more</span>
      {/if}
    {/if}
  </div>
</div>

<style>
  /* Outer wrapper — only meaningful when `label` is true (stacks caption above
     chips); a bare no-label instance (the two pre-existing callers) still gets
     this wrapper but it's a no-op single-child box, pixel-identical to before. */
  .sg-set {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  /* Compact caption (VP panel fix): smaller than SetRibbon's 15px .set-label on
     purpose — Scott wants the club glance MORE condensed, not Swim-tab-sized. */
  .sg-label {
    margin: 0;
    font-size: 12px;
    line-height: 1.25;
    font-weight: 500;
    color: var(--text-muted);
  }
  .sg-label strong {
    color: var(--accent-primary);
    font-weight: 600;
  }
  .chips-row {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
  }
  /* Drill view: bigger chips + gap so the set reads as a hero picture. */
  .chips-row.large {
    gap: 5px;
  }
  .chip {
    /* border-box so the 1px hairline does not change the chip footprint. */
    box-sizing: border-box;
    width: 13px;
    height: 22px;
    border-radius: 3px;
    flex-shrink: 0;
    /* Crisp hairline so every chip's geometry reads on the near-black canvas. */
    border: 1px solid rgba(255, 255, 255, 0.28);
  }
  .chips-row.large .chip {
    width: 26px;
    height: 44px;
    border-radius: 5px;
  }
  .chips-row.letters .chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .chip-letter {
    font-family: var(--font-text);
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
  }
  .chip-lone {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    opacity: 0.6;
    /* VP panel fix (club-pool-swims round): this chip had NO hairline border,
       unlike every regular .chip — sitting inline among bordered 13x22 chips
       with no label and no border, a real single-length set read as a clipped
       or broken chip, not an intentional "this one's different" glance. The
       label above (see `label` prop) now names it explicitly; the border
       closes the other half of the gap so it reads as styled, not glitched. */
    border: 1px solid rgba(255, 255, 255, 0.28);
  }
  .chips-row.large .chip-lone {
    width: 32px;
    height: 32px;
    border-radius: 7px;
  }
  .chip-more {
    width: auto;
    min-width: 28px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-raised);
    color: var(--text-muted);
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /* Single-stroke band — one solid rounded swatch standing in for a run of
     identical chips, with a "×N" length count overlaid. */
  .band {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 56px;
    height: 22px;
    padding: 0 10px;
    border-radius: 5px;
    border: 1px solid rgba(255, 255, 255, 0.28);
  }
  .chips-row.large .band {
    min-width: 96px;
    height: 44px;
    border-radius: 8px;
  }
  .band-count {
    font-size: 12px;
    font-weight: 700;
    color: #E4E2E4;
    font-variant-numeric: tabular-nums;
    /* Lift the count off any light band fill. */
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
  }
  .chips-row.large .band-count {
    font-size: 18px;
  }
</style>
